import React from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import { rupiah } from '@lib/currency';
import {
  AccountBindingInfo,
  DataDetailCheckout,
  SettingsProps,
} from '@pages/types';
import Spinner from '@components/Spinner';
import HeaderResult from '@components/Result/HeaderResult';
import TimeCountdown from './TimeCountdown';
import { RenderBankLogoName } from '@components/RenderBankPaymentMethod';
import UnbindButton from '@components/UnbindButton';

interface DanaBindingProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  accountBinding?: AccountBindingInfo;
  submittedCustomerNo?: string | null;
  danaBalanceValue: string;
  danaBalanceCurrency: string;
  danaBalanceError: string;
  danaBalanceFetched: boolean;
  danaBalanceLoading: boolean;
  isLoadingSubmit: boolean;
  onGetBalance: () => void;
  onContinuePayment: () => void;
  onGoBack: () => void;
}

const DanaBinding: React.FC<DanaBindingProps> = ({
  data,
  settings,
  accountBinding,
  submittedCustomerNo,
  danaBalanceValue,
  danaBalanceCurrency,
  danaBalanceError,
  danaBalanceFetched,
  danaBalanceLoading,
  isLoadingSubmit,
  onGetBalance,
  onContinuePayment,
  onGoBack,
}) => {
  const parsedDanaBalance = Number.parseFloat(
    String(danaBalanceValue || '0').replace(/,/g, '')
  );
  const invoiceTotal = Number(data?.total || 0);
  const isInsufficientBalance =
    danaBalanceFetched &&
    !danaBalanceError &&
    (Number.isNaN(parsedDanaBalance) ? 0 : parsedDanaBalance) < invoiceTotal;

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <button
        type="button"
        onClick={onGoBack}
        disabled={isLoadingSubmit || danaBalanceLoading}
        className="flex w-max items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <IconArrowLeft className="h-5 w-5" />
        <span className="font-medium">Back to Payment Methods</span>
      </button>

      <HeaderResult
        title="DANA Account Binding"
        subtitle="Check your DANA balance before continuing payment"
      />

      <TimeCountdown expirationTime={data?.payment_link_expiration} />

      <div>
        <RenderBankLogoName
          title={accountBinding?.payment_channel || 'Not Found'}
          width="w-[120px]"
          height="h-[50px]"
        />
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 p-6 shadow-sm">
          <p className="text-sm text-gray-500">Payment sent to phone number</p>
          <p className="text-2xl font-bold tracking-wide text-gray-900">
            {accountBinding?.account_no || submittedCustomerNo || '-'}
          </p>
          <p className="text-xs text-gray-400">
            Registered phone number for{' '}
            {accountBinding?.payment_channel || 'DANA'}
          </p>
        </div>

        {(danaBalanceValue || danaBalanceError) && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-500">Balance</span>
              {danaBalanceError ? (
                <span className="text-sm text-red-600">{danaBalanceError}</span>
              ) : (
                <span className="text-2xl font-semibold text-gray-900">
                  {rupiah(Number(danaBalanceValue) || 0)}
                </span>
              )}
              <span className="text-xs text-gray-500">
                Currency: {danaBalanceCurrency}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row">
          {!danaBalanceFetched ? (
            <button
              type="button"
              onClick={onGetBalance}
              disabled={danaBalanceLoading || isLoadingSubmit}
              className="flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: settings.button_color || '#5D87FF',
                color: settings.button_text_color || '#FFFFFF',
              }}
            >
              {danaBalanceLoading ? <Spinner /> : 'Get Balance'}
            </button>
          ) : (
            <button
              type="button"
              onClick={onContinuePayment}
              disabled={
                isLoadingSubmit || danaBalanceLoading || isInsufficientBalance
              }
              className="flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: settings.button_color || '#5D87FF',
                color: settings.button_text_color || '#FFFFFF',
              }}
            >
              {isLoadingSubmit ? <Spinner /> : 'Continue to Payment'}
            </button>
          )}
          {accountBinding && (
            <UnbindButton
              accountBinding={accountBinding}
              disabled={danaBalanceLoading || isLoadingSubmit}
              settings={settings}
              onGoBack={onGoBack}
            />
          )}
        </div>

        {isInsufficientBalance && (
          <p className="text-sm text-red-600">
            Balance DANA tidak cukup untuk melanjutkan pembayaran.
          </p>
        )}
      </div>
    </div>
  );
};

export default DanaBinding;
